import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type CatalogDTO } from "../../api";
import { crudEditor, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/** Catalogs are a one-column reference list: a name, unique across the table. */
export function CatalogsPage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.CONFIG_CATALOG);
  const title = t("navigation.item.catalogs", "Catalogs");

  const { data, isLoading, error } = useQuery({
    queryKey: ["catalog"],
    queryFn: () => api.catalogs.findAll(),
    enabled: allowed,
  });

  const rows = data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["catalog"] });

  const cols = useMemo(() => {
    const c = defineColumns<CatalogDTO>();
    return [c.text("name", (row) => row.name, { headerKey: "column.name", priority: 1 })];
  }, []);

  const editor = useMemo(
    () =>
      crudEditor<CatalogDTO>({
        client: api.catalogs,
        refresh,
        t,
        fields: {
          name: textField(
            (row) => row.name,
            (row, value) => ({ ...row, name: value }),
            true,
          ),
        },
        newRow: () => ({ id: 0, name: "" }),
        validate: (row) => {
          if (!row.name.trim()) return { name: t("form.validation.field.required", "Value is required") };
          if (rows.some((entry) => entry.id !== row.id && entry.name === row.name)) {
            return { name: t("form.validation.field.unique", "Value already exists") };
          }
          return null;
        },
      }),
    [api, rows, t],
  );

  if (!allowed) return <AccessDenied title={title} />;
  if (error) return <ErrorState title={title} error={error} />;

  return (
    <>
      <PageHeader title={title} />
      <DataTable<CatalogDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        editor={editor}
      />
    </>
  );
}
